import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useAnalytics } from '@vaptmenu/analytics-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { logout as logoutApi } from '@/features/auth/api/auth.api';

/**
 * Convenience hook exposing auth state and permission helpers.
 */
export function useAuth() {
  const {
    accessToken,
    user,
    tenants,
    selectedTenant,
    setTokens,
    setTenants,
    setSelectedTenant,
    clearAuth,
  } = useAuthStore();

  const navigate = useNavigate();
  const { track } = useAnalytics();

  const isAuthenticated = !!accessToken && !!user;

  const hasPermission = (permission: string): boolean => {
    return user?.permissions.includes(permission) ?? false;
  };

  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  const hasAnyPermission = (...permissions: string[]): boolean => {
    return permissions.some((p) => hasPermission(p));
  };

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } catch {
      // Best-effort — always clear local state
    }
    // Antes do clearAuth, para o evento ainda sair com a identidade;
    // o reset da identidade acontece no AnalyticsIdentitySync (user → null).
    track('panel.signed_out');
    clearAuth();
    navigate('/login', { replace: true });
  }, [clearAuth, navigate, track]);

  return {
    user,
    accessToken,
    tenants,
    selectedTenant,
    isAuthenticated,
    hasPermission,
    hasRole,
    hasAnyPermission,
    setTokens,
    setTenants,
    setSelectedTenant,
    logout,
  };
}
